import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;
const ADD_TO_CART_SELECTOR = '.product-page form button[name="add"].button--add-to-cart';

/**
 * Bridges Flex PDP add-to-cart to Horizon's cart drawer, live-region announcements,
 * and focus management (dialog pattern, focus trap, Escape handled by theme-drawer).
 */
class AkFlexCartBridge {
  /** @type {boolean} */
  #bound = false;

  /** @type {number | undefined} */
  #announceTimeout;

  init() {
    if (this.#bound || !document.querySelector('.product-page')) return;
    this.#bound = true;

    document.addEventListener('click', this.#onAddToCartClick, true);
    document.addEventListener('submit', this.#onProductFormSubmit, true);
  }

  /**
   * @param {Element | EventTarget | null} target
   * @returns {HTMLButtonElement | null}
   */
  #findAddToCartButton(target) {
    if (!(target instanceof Element)) return null;
    const button = target.closest(ADD_TO_CART_SELECTOR);
    return button instanceof HTMLButtonElement ? button : null;
  }

  /** @param {MouseEvent} event */
  #onAddToCartClick = (event) => {
    const button = this.#findAddToCartButton(event.target);
    if (!button) return;

    const form = button.closest('form');
    if (!(form instanceof HTMLFormElement)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (button.disabled || button.classList.contains('disabled')) return;

    void this.#addToCart(form, button);
  };

  /** @param {SubmitEvent} event */
  #onProductFormSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.closest('.product-page')) return;

    const button = form.querySelector('button[name="add"].button--add-to-cart');
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.type === 'button') return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (button.disabled || button.classList.contains('disabled')) return;

    void this.#addToCart(form, button);
  };

  /**
   * @param {HTMLFormElement} form
   * @param {HTMLButtonElement} button
   */
  async #addToCart(form, button) {
    const formData = new FormData(form);
    const variantId = formData.get('id');

    if (!variantId) {
      const message = this.#getSelectVariantMessage(form);
      this.#showError(form, button, message);
      return;
    }

    const itemCount = Number(formData.get('quantity')) || 1;
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
    }

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          {
            merchandiseId: String(variantId),
            quantity: itemCount,
          },
        ],
        promise: deferredEventPromise.promise,
      })
    );

    this.#setButtonLoading(button, true);

    try {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });

      const payload = await response.json();

      if (payload.status) {
        const message = payload.message || payload.description || 'Unable to add item to cart.';
        this.#showError(form, button, message);

        document.dispatchEvent(
          new CartErrorEvent({
            error: message,
            code: 'INVALID',
            detail: {
              description: payload.description,
              errors: payload.errors,
            },
          })
        );

        const cart = await this.#refreshCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: true,
            items: cart.items,
            source: 'ak-flex-cart-bridge',
            itemCount,
          },
        });
        return;
      }

      const cart = await this.#refreshCart();
      const cartCount = cart.item_count ?? cart.items?.reduce((sum, item) => sum + item.quantity, 0) ?? itemCount;

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'ak-flex-cart-bridge',
          itemCount,
          sections: payload.sections,
        },
      });

      this.#clearError(form);
      this.#announceSuccess(cartCount);
      await this.#openCartDrawerWhenReady();
    } catch (error) {
      console.error('[ak-flex-cart-bridge]', error);
      const message = error instanceof Error ? error.message : 'Unable to add item to cart.';

      this.#showError(form, button, message);
      deferredEventPromise.reject(error);

      document.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.#setButtonLoading(button, false);
    }
  }

  /**
   * @param {HTMLFormElement} form
   */
  #getSelectVariantMessage(form) {
    const warning = form.querySelector('.cart-warning');
    const existing = warning?.textContent?.trim();
    if (existing) return existing;
    return 'Please select a size before adding to cart.';
  }

  /**
   * @param {HTMLFormElement} form
   * @param {HTMLButtonElement} button
   * @param {string} message
   */
  #showError(form, button, message) {
    const warning = form.querySelector('.cart-warning');
    if (warning instanceof HTMLElement) {
      warning.textContent = message;
      warning.setAttribute('role', 'alert');
    }

    this.#announce(message, 'assertive');
    button.focus();
  }

  /** @param {HTMLFormElement} form */
  #clearError(form) {
    const warning = form.querySelector('.cart-warning');
    if (!(warning instanceof HTMLElement)) return;
    warning.textContent = '';
    warning.removeAttribute('role');
  }

  /** @param {number} cartCount */
  #announceSuccess(cartCount) {
    const added = Theme.translations.added || 'Added to cart';
    const countLabel = Theme.translations.cart_count || 'Cart count';
    this.#announce(`${added}. ${countLabel}: ${cartCount}`, 'assertive');
  }

  /**
   * @param {string} message
   * @param {'polite' | 'assertive'} politeness
   */
  #announce(message, politeness) {
    const liveRegion = this.#getLiveRegion();
    liveRegion.setAttribute('aria-live', politeness);
    liveRegion.textContent = '';

    window.requestAnimationFrame(() => {
      liveRegion.textContent = message;
    });

    clearTimeout(this.#announceTimeout);
    this.#announceTimeout = window.setTimeout(() => {
      liveRegion.textContent = '';
    }, politeness === 'assertive' ? ERROR_MESSAGE_DISPLAY_DURATION : SUCCESS_MESSAGE_DISPLAY_DURATION);
  }

  /** @returns {HTMLElement} */
  #getLiveRegion() {
    let liveRegion = document.getElementById('ak-pdp-cart-live-region');
    if (liveRegion instanceof HTMLElement) return liveRegion;

    liveRegion = document.createElement('div');
    liveRegion.id = 'ak-pdp-cart-live-region';
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    document.body.appendChild(liveRegion);
    return liveRegion;
  }

  async #refreshCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.status}`);
    }

    return response.json();
  }

  async #openCartDrawerWhenReady() {
    await new Promise((resolve) => {
      window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
    });

    const themeDrawer = document.getElementById('cart-drawer');
    if (!(themeDrawer instanceof HTMLElement) || typeof themeDrawer.open !== 'function') return;

    if (themeDrawer.hasAttribute('open')) {
      this.#focusCartDrawer(themeDrawer);
      return;
    }

    await new Promise((resolve) => {
      themeDrawer.addEventListener(DrawerOpenEvent.eventName, () => resolve(), { once: true });
      themeDrawer.open();
    });

    this.#focusCartDrawer(themeDrawer);
  }

  /** @param {HTMLElement} themeDrawer */
  #focusCartDrawer(themeDrawer) {
    const closeButton = themeDrawer.querySelector('.theme-drawer__close-button');
    if (closeButton instanceof HTMLElement) {
      closeButton.focus();
    }
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} loading
   */
  #setButtonLoading(button, loading) {
    button.disabled = loading;
    button.classList.toggle('disabled', loading);
    button.setAttribute('aria-busy', loading ? 'true' : 'false');
  }
}

const bridge = new AkFlexCartBridge();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => bridge.init(), { once: true });
} else {
  bridge.init();
}
